import { useSelector } from 'react-redux'

import { CardContainer } from '@components/UI/Card/Card'
import { ICampaignContextProps } from '@context/Campaign'
import { ISelectedSubmissionsContextProps } from '@context/SelectedSubmissions'
import { ISubmissionListFilterContextProps } from '@context/SubmissionListFilter'
import {
  withAnalyticsTracking,
  withCampaign,
  withSelectedSubmissions,
  withSubmissionListFilter,
} from '@hocs'
import hooks from '@hooks'
import { IApplicationState } from '@store'
import { IMediaObject } from '@store/mediaObjects'
import {
  selectActiveSubmissionMediaObject,
  selectShowBrandedContentAdBar,
  selectSubmission,
} from '@store/submissions'
import { Container } from '@tribegroup'
import { isSocialCampaign } from '@utils/campaign'
import SubmissionCardHeader from './Header/Header'
import SubmissionLinkToDetails from './LinkToDetails/LinkToDetails'
import SubmissionCardThumbnail from './Thumbnail/Thumbnail'
import SubmissionCardActionBar from './ActionBar'
import { SubmissionCardBrandedContentAdBar } from './BrandedContentAd'
import SubmissionCardMoreMenu from './MoreMenu'
import SubmissionCardPriceSummary from './PriceSummary'

export interface IProps {
  submissionId: number
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void
}

interface IInternalProps
  extends IProps,
    ICampaignContextProps,
    ISelectedSubmissionsContextProps,
    ISubmissionListFilterContextProps {}

const SubmissionCard: React.FC<IInternalProps> = ({
  submissionId,
  campaign,
  selectedSubmissionIds,
  isFilterLicenseRelated,
  isSelectionModeOn,
}) => {
  const { toggleIsVisible, toggleHide, toggleShow, toggle } = hooks.useToggle()
  const { id: mediaObjectId } = hooks.useSelector<IMediaObject>((state: IApplicationState) =>
    selectActiveSubmissionMediaObject(state, submissionId),
  )
  const isSocial = isSocialCampaign(campaign)
  const campaignId = campaign.id
  const submission = useSelector((state: IApplicationState) =>
    selectSubmission(state, submissionId),
  )
  const selected = selectedSubmissionIds.includes(submissionId)
  const showLicenseRequestAsApprove = !isSocial && !isFilterLicenseRelated
  const showBrandedContentAdBar = useSelector((state: IApplicationState) =>
    selectShowBrandedContentAdBar(state, submissionId, isSocial),
  )

  return (
    <CardContainer
      selected={selected}
      elementName="submission-card"
      outerContent={
        isSocial ? (
          <SubmissionCardMoreMenu submissionId={submissionId} showCostBreakdown={toggleShow} />
        ) : undefined
      }
    >
      <SubmissionCardHeader
        campaignId={campaignId}
        submissionId={submissionId}
        toggleCostBreakdown={toggle}
        enableCostBreakdownTracking={toggleIsVisible}
      />
      <SubmissionLinkToDetails submissionId={submission.id} selected={selected}>
        <SubmissionCardThumbnail submissionId={submission.id} />
      </SubmissionLinkToDetails>
      {showBrandedContentAdBar && <SubmissionCardBrandedContentAdBar submissionId={submissionId} />}
      <SubmissionCardActionBar
        submissionId={submission.id}
        showLicenseRequestAsApprove={showLicenseRequestAsApprove}
        isSelectionModeOn={isSelectionModeOn}
      />
      {toggleIsVisible && (
        <Container absolute leftPosition={0} topPosition={4} fullWidth>
          <SubmissionCardPriceSummary
            mediaObjectId={mediaObjectId}
            campaignId={campaignId}
            submissionId={submissionId}
            onHide={toggleHide}
          />
        </Container>
      )}
    </CardContainer>
  )
}

export { SubmissionCard }
export default withAnalyticsTracking(
  withSubmissionListFilter(withSelectedSubmissions(withCampaign(SubmissionCard))),
)
